// AI Generated. Ported from docs/camping/completed/validation-poc.jsx — JS, no React/DOM dependencies.

/**
 * Returns true when `allocation` satisfies all constraints defined by `rule`.
 * allocation: { [paxTypeKey]: number }
 * rule: { minimumPartySize, maximumPartySize, paxRules: [{ paxTypeKey, minimumPaxCount, maximumPaxCount }] }
 */
export function doesAllocationMatchRule(allocation, totalOccupants, rule) {
  if (totalOccupants < rule.minimumPartySize) return false;
  if (totalOccupants > rule.maximumPartySize) return false;

  const constraintsSatisfied = (rule.paxRules || []).every((constraint) => {
    const count = allocation[constraint.paxTypeKey] ?? 0;
    return count >= constraint.minimumPaxCount && count <= constraint.maximumPaxCount;
  });
  if (!constraintsSatisfied) return false;

  // Any pax type present but NOT listed in paxRules is implicitly disallowed.
  const allowedKeys = new Set((rule.paxRules || []).map((c) => c.paxTypeKey));
  return !Object.entries(allocation).some(([key, count]) => count > 0 && !allowedKeys.has(key));
}

// Returns all ways to distribute `total` pax across `tentCount` tents,
// each capped at the corresponding capacity entry.
function generateDistributions(total, tentCount, capacities) {
  if (tentCount === 1) {
    return total <= capacities[0] ? [[total]] : [];
  }
  const results = [];
  const maxForThis = Math.min(total, capacities[0]);
  for (let i = 0; i <= maxForThis; i += 1) {
    generateDistributions(total - i, tentCount - 1, capacities.slice(1)).forEach((rest) => {
      results.push([i, ...rest]);
    });
  }
  return results;
}

// Returns true if `allocation` is valid for the given tent instance.
// An empty tent (zero occupants) is always valid — parties need not fill every tent.
function isValidTentAllocationQuick(allocation, tent) {
  const total = Object.values(allocation).reduce((s, c) => s + c, 0);
  if (total === 0) return true;
  if (total > (tent.option?.numberOfBerths || 0)) return false;
  const rulesets = tent.option?.paxOccupancyRuleSets;
  if (!rulesets?.length) return true; // unconstrained tent
  return rulesets.some((rule) => doesAllocationMatchRule(allocation, total, rule));
}

// Checks whether the party can be distributed across tentInstances such that every
// tent's allocation satisfies its paxOccupancyRuleSets. Short-circuits on the first
// valid distribution found (ported from validation-poc.jsx checkFeasibility).
function checkFeasibility(paxSelection, tentInstances) {
  const paxTypeKeys = Object.keys(paxSelection).filter((k) => paxSelection[k] > 0);
  const capacities = tentInstances.map((t) => t.option?.numberOfBerths || 0);
  let found = false;

  function distribute(paxTypeIndex, currentAllocations) {
    if (found) return;
    if (paxTypeIndex >= paxTypeKeys.length) {
      if (
        currentAllocations.every((alloc, i) => isValidTentAllocationQuick(alloc, tentInstances[i]))
      ) {
        found = true;
      }
      return;
    }

    const currentKey = paxTypeKeys[paxTypeIndex];
    const distributions = generateDistributions(
      paxSelection[currentKey],
      tentInstances.length,
      capacities
    );

    distributions.some((dist) => {
      if (found) return true;
      const newAllocations = currentAllocations.map((alloc, i) => ({
        ...alloc,
        ...(dist[i] > 0 ? { [currentKey]: dist[i] } : {}),
      }));
      const exceedsCapacity = newAllocations.some((alloc, i) => {
        const total = Object.values(alloc).reduce((s, c) => s + c, 0);
        return total > capacities[i];
      });
      if (!exceedsCapacity) distribute(paxTypeIndex + 1, newAllocations);
      return found;
    });
  }

  distribute(
    0,
    tentInstances.map(() => ({}))
  );
  return { feasible: found };
}

/**
 * runFeasibility — checks whether the party can be accommodated in the selected tents.
 *
 * Each { tent, count } entry is expanded into `count` individual tent instances.
 * The algorithm then checks whether the party can be distributed across those instances
 * such that every tent's allocation satisfies its paxOccupancyRuleSets.
 *
 * @param {Array<{ tent: object, count: number }>} selectedTents
 *   tent.option.numberOfBerths: number
 *   tent.option.paxOccupancyRuleSets: Array<{ minimumPartySize, maximumPartySize, paxRules }>
 * @param {{ [paxTypeKey: string]: number }} paxSelection
 * @returns {{ isValid: boolean, reason?: string }}
 *   reason: 'NO_TENTS_SELECTED' | 'INSUFFICIENT_CAPACITY' | 'PAX_COMBINATION_NOT_SUPPORTED'
 */
export function runFeasibility(selectedTents, paxSelection) {
  if (!selectedTents?.length) {
    return { isValid: false, reason: 'NO_TENTS_SELECTED' };
  }

  // Expand { tent, count } into individual instances (e.g. count:2 → two tent objects).
  const tentInstances = selectedTents.flatMap(({ tent, count }) =>
    Array.from({ length: count }, () => tent)
  );

  const totalPax = Object.values(paxSelection || {}).reduce((s, n) => s + n, 0);
  const totalBerths = tentInstances.reduce(
    (sum, tent) => sum + (tent.option?.numberOfBerths || 0),
    0
  );

  if (totalBerths < totalPax) {
    return { isValid: false, reason: 'INSUFFICIENT_CAPACITY' };
  }

  const { feasible } = checkFeasibility(paxSelection || {}, tentInstances);
  if (!feasible) {
    return { isValid: false, reason: 'PAX_COMBINATION_NOT_SUPPORTED' };
  }

  return { isValid: true };
}
